import { usePayments } from "../../../../../hooks/usePayments";
import { useAnimals } from "../../../../../hooks/useAnimals";
import type { PaymentRecord } from "../../../../../mocks/payments";
import type { ShareItem } from "../../../../../mocks/animals";

interface Props {
  share: ShareItem;
  animalId: number;
  onClose: () => void;
}

const fmt = (n: number) =>
  n.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });

const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const tipStyle: Record<string, string> = {
  Nakit: "bg-farm-green-pale text-farm-green",
  Havale: "bg-farm-gold-pale text-farm-gold",
};

const tipIcon: Record<string, string> = {
  Nakit: "ri-money-cny-circle-line",
  Havale: "ri-bank-line",
};

export default function SharePaymentDrawer({ share, animalId, onClose }: Props) {
  const { payments } = usePayments();
  const { animals } = useAnimals();
  const ownerAnimal = animals.find((animal) => Number(animal.id) === Number(animalId));
  const animalTypeLabel = ownerAnimal?.tur === "Büyükbaş" ? "B.Baş" : ownerAnimal?.tur === "Küçükbaş" ? "K.Baş" : "—";

  const sharePayments = payments.filter(
    (p: PaymentRecord) => p.shareId === share.id && p.animalId === animalId
  );

  const toplamUcret =
    share.tutar +
    (share.kesimUcreti ?? 0) +
    (share.parcalamaUcreti ?? 0) +
    (share.tasimaUcreti ?? 0) -
    (share.indirim ?? 0);

  const toplamOdenen =
    sharePayments.length > 0
      ? sharePayments.reduce((sum: number, p: PaymentRecord) => sum + p.tutar, 0)
      : share.odenen;

  const hasSyntheticOnly = sharePayments.length === 0 && share.odenen > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-end bg-black/30 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full sm:w-96 h-full sm:h-auto sm:max-h-[90vh] bg-farm-warm border-l border-farm-border flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-farm-border bg-farm-cream flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-6 h-6 flex items-center justify-center text-farm-earth flex-shrink-0">
              <i className="ri-file-list-3-line text-base" />
            </div>
            <div className="min-w-0">
              <p className="font-heading font-semibold text-sm text-farm-brown truncate">
                Ödeme Geçmişi
              </p>
              <p className="font-body text-[10px] text-farm-muted truncate">
                {share.musteriAdi} · {animalTypeLabel} · {share.hisseNo}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center text-farm-muted hover:text-farm-brown rounded-full hover:bg-farm-cream transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>

        <div className="px-4 py-3 border-b border-farm-border bg-farm-cream/50 flex-shrink-0">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-farm-warm rounded px-2.5 py-2 text-center border border-farm-border">
              <p className="font-body text-[10px] text-farm-muted mb-0.5">Toplam Borç</p>
              <p className="font-body text-xs font-bold text-farm-brown">{fmt(toplamUcret)}</p>
            </div>

            <div className="bg-farm-green-pale/60 rounded px-2.5 py-2 text-center border border-farm-green/15">
              <p className="font-body text-[10px] text-farm-muted mb-0.5">Ödenen</p>
              <p className="font-body text-xs font-bold text-farm-green">{fmt(toplamOdenen)}</p>
            </div>

            <div
              className={`rounded px-2.5 py-2 text-center border ${
                share.kalan > 0
                  ? "bg-farm-earth-pale/60 border-farm-earth/15"
                  : "bg-farm-cream border-farm-border"
              }`}
            >
              <p className="font-body text-[10px] text-farm-muted mb-0.5">Kalan</p>
              <p
                className={`font-body text-xs font-bold ${
                  share.kalan > 0 ? "text-farm-earth" : "text-farm-muted"
                }`}
              >
                {fmt(share.kalan)}
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {sharePayments.length === 0 ? (
            hasSyntheticOnly ? (
              <div className="flex flex-col gap-3">
                <div className="px-3 py-2.5 rounded-md bg-farm-gold-pale border border-farm-gold/20">
                  <p className="font-body text-xs text-farm-muted leading-relaxed">
                    Bu hisse için detay ödeme hareketi kaydı henüz oluşturulmamış.
                    Ancak hisse kartındaki toplam ödenen tutar doğru şekilde gösteriliyor.
                  </p>
                </div>

                <div className="bg-farm-cream border border-farm-border rounded-farm overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-farm-border/50">
                    <div className="flex items-center gap-2">
                      <span className="font-body text-[10px] text-farm-muted font-semibold">
                        Özet
                      </span>
                      <span className="font-body text-xs font-bold text-farm-brown">
                        {fmt(share.odenen)}
                      </span>
                    </div>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap bg-farm-cream text-farm-muted border border-farm-border">
                      Toplam Ödenen
                    </span>
                  </div>

                  <div className="px-3 py-2 flex flex-col gap-1">
                    <div className="flex items-center gap-1.5">
                      <div className="w-3.5 h-3.5 flex items-center justify-center text-farm-muted flex-shrink-0">
                        <i className="ri-information-line text-xs" />
                      </div>
                      <span className="font-body text-xs text-farm-muted italic">
                        Ayrıntılı ödeme satırları henüz kaydedilmediği için bu tutar hisse kaydından okunuyor.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="w-10 h-10 flex items-center justify-center text-farm-muted mb-2">
                  <i className="ri-inbox-line text-3xl" />
                </div>
                <p className="font-body text-xs text-farm-muted">Henüz ödeme kaydı yok</p>
              </div>
            )
          ) : (
            <div className="flex flex-col gap-2">
              <p className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-1">
                {sharePayments.length} ödeme kaydı
              </p>

              {sharePayments.map((p: PaymentRecord, idx: number) => (
                <div
                  key={p.id}
                  className="bg-farm-cream border border-farm-border rounded-farm overflow-hidden"
                >
                  <div className="flex items-center justify-between px-3 py-2 border-b border-farm-border/50">
                    <div className="flex items-center gap-2">
                      <span className="font-body text-[10px] text-farm-muted font-semibold">
                        #{idx + 1}
                      </span>
                      <span className="font-body text-xs font-bold text-farm-brown">
                        {fmt(p.tutar)}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${
                          tipStyle[p.tip] ?? "bg-farm-cream text-farm-muted"
                        }`}
                      >
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className={`${tipIcon[p.tip] ?? "ri-money-dollar-circle-line"} text-[10px]`} />
                        </div>
                        {p.tip}
                      </span>
                    </div>
                  </div>

                  <div className="px-3 py-2 flex flex-col gap-1">
                    <div className="flex items-center gap-1.5">
                      <div className="w-3.5 h-3.5 flex items-center justify-center text-farm-muted flex-shrink-0">
                        <i className="ri-calendar-line text-xs" />
                      </div>
                      <span className="font-body text-xs text-farm-brown">{fmtDate(p.tarih)}</span>
                    </div>

                    {p.havaleNo && (
                      <div className="flex items-center gap-1.5">
                        <div className="w-3.5 h-3.5 flex items-center justify-center text-farm-muted flex-shrink-0">
                          <i className="ri-hashtag text-xs" />
                        </div>
                        <span className="font-body text-xs text-farm-muted">{p.havaleNo}</span>
                      </div>
                    )}

                    {p.aciklama && (
                      <div className="flex items-center gap-1.5">
                        <div className="w-3.5 h-3.5 flex items-center justify-center text-farm-muted flex-shrink-0">
                          <i className="ri-chat-1-line text-xs" />
                        </div>
                        <span className="font-body text-xs text-farm-muted italic">{p.aciklama}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-1.5">
                      <div className="w-3.5 h-3.5 flex items-center justify-center text-farm-muted flex-shrink-0">
                        <i className="ri-user-line text-xs" />
                      </div>
                      <span className="font-body text-[10px] text-farm-muted">{p.olusturan}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-farm-border flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-2 rounded-full border border-farm-border text-farm-muted font-body text-xs font-semibold hover:bg-farm-cream transition-colors whitespace-nowrap cursor-pointer"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
}